import React, { forwardRef } from "react";
import { money } from "@/lib/api";

const LOCATION_LABELS = {
  growth: "Growth corridor", schools: "Good schools", childcare: "Childcare",
  transport: "Transport links", work: "Close to work", shops: "Shops & dining",
  parks: "Parks & nature", medical: "Medical & hospitals",
};

const S = {
  eyebrow: { fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: "#C05A46", fontWeight: 700, marginTop: 18 },
  card: { marginTop: 8, background: "#F7F5EF", border: "1px solid #E8E2D2", borderRadius: 12, padding: 16 },
  row: { padding: "3px 0", fontSize: 13, color: "#68736E" },
  val: { padding: "3px 0", fontSize: 13, color: "#1E3B32", textAlign: "right", fontWeight: 600 },
};

function KV({ items }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <tbody>
        {items.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([l, v]) => (
          <tr key={l}><td style={S.row}>{l}</td><td style={S.val}>{v}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

// Full consultant dossier — every captured detail for follow-up. Rendered
// off-screen and captured by html2pdf.js.
export const ConsultantDossier = forwardRef(function ConsultantDossier({ session }, ref) {
  const data = session?.data || {};
  const isInvestor = session?.client_type === "investor";
  const buyers = (data.profile?.buyers || []).filter((b) => b.name || b.email || b.mobile);
  const answers = data.discovery_answers || {};
  const lp = data.life_profile || {};
  const loc = data.location || {};
  const ranked = (loc.needs || []).map((k, i) => `${i + 1}. ${LOCATION_LABELS[k] || k}`);
  const fin = data.finance || {};
  const finIn = fin.inputs || {};
  const budget = data.budget || {};
  const selected = data.selected_home || {};
  const chosen = (data.matches || {})[selected.kind] || {};
  const wealth = data.wealth || {};
  const rewards = data.rewards || [];
  const voucher = data.voucher || {};
  const iDisc = data.investor_discovery || {};
  const iLoc = data.investor_location || {};
  const iFund = data.investor_funding || {};
  const iFloor = data.investor_floorplan || {};

  return (
    <div style={{ position: "fixed", left: "-9999px", top: 0 }}>
      <div ref={ref} style={{ width: "794px", background: "#FFFFFF", color: "#1E3B32", fontFamily: "Arial, Helvetica, sans-serif", padding: "40px 44px" }}>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#C05A46", fontWeight: 700 }}>Consultant Dossier · Confidential</div>
        <h1 style={{ fontSize: 26, fontWeight: 400, margin: "8px 0 2px" }}>{session?.client_name || "Client"}</h1>
        <div style={{ fontSize: 13, color: "#68736E" }}>
          {isInvestor ? "Investor" : session?.client_type === "first_home_buyer" ? "First Home Buyer" : "Home Buyer"} ·
          Consultant: {session?.consultant_name} · Status: {session?.status} ·
          Created: {session?.created_at ? new Date(session.created_at).toLocaleDateString("en-AU") : "—"}
        </div>

        <div style={S.eyebrow}>Contacts</div>
        <div style={S.card}>
          {buyers.length ? buyers.map((b, i) => (
            <div key={i} style={{ fontSize: 13, padding: "3px 0" }}>
              <strong>{b.name || `Buyer ${i + 1}`}</strong> — {b.email || "no email"} · {b.mobile || "no mobile"}
              {b.contact ? ` · prefers ${b.contact}` : ""}
            </div>
          )) : <div style={{ fontSize: 13, color: "#68736E" }}>No contact captured yet.</div>}
          {data.profile?.postcode ? <div style={{ fontSize: 13, color: "#68736E", marginTop: 4 }}>Postcode: {data.profile.postcode}</div> : null}
        </div>

        {!isInvestor && (
          <>
            {Object.keys(answers).length > 0 && (
              <>
                <div style={S.eyebrow}>Discovery answers</div>
                <div style={S.card}>
                  {Object.entries(answers).map(([q, a]) => (
                    <div key={q} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: "#C05A46", fontWeight: 700 }}>{q}</div>
                      <div style={{ fontSize: 13 }}>{a || "—"}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {(lp.lifestyle_statement || (lp.tags && lp.tags.length)) && (
              <>
                <div style={S.eyebrow}>Life profile</div>
                <div style={S.card}>
                  {lp.lifestyle_statement ? <div style={{ fontSize: 13, marginBottom: 6 }}>{lp.lifestyle_statement}</div> : null}
                  {lp.tags?.length ? <div style={{ fontSize: 12, color: "#68736E" }}>Tags: {lp.tags.join(", ")}</div> : null}
                </div>
              </>
            )}
            {(ranked.length || loc.notes) && (
              <>
                <div style={S.eyebrow}>Location priorities</div>
                <div style={S.card}>
                  <div style={{ fontSize: 13 }}>{ranked.length ? ranked.join("   ·   ") : "None captured."}</div>
                  {loc.notes ? <div style={{ fontSize: 13, color: "#68736E", marginTop: 6 }}>Notes: “{loc.notes}”</div> : null}
                </div>
              </>
            )}
            <div style={S.eyebrow}>Finance</div>
            <div style={S.card}>
              <KV items={[
                ["Applicant income", finIn.annual_income ? money(finIn.annual_income) : undefined],
                ["Partner income", finIn.partner_income ? money(finIn.partner_income) : undefined],
                ["Dependants", finIn.dependants],
                ["Monthly commitments", finIn.monthly_commitments ? money(finIn.monthly_commitments) : undefined],
                ["Deposit", finIn.deposit ? money(finIn.deposit) : undefined],
                ["Borrowing (mid)", fin.borrowing_mid ? money(fin.borrowing_mid) : undefined],
                ["Assessed HEM (monthly)", fin.hem_monthly ? money(fin.hem_monthly) : undefined],
              ]} />
            </div>
            <div style={S.eyebrow}>Budget</div>
            <div style={S.card}>
              <KV items={[
                ["Home budget", money(budget.home_budget)],
                ["Property value", money(budget.property_value)],
                ["Land price", money(budget.land_price)],
                ["Deposit", money(budget.deposit)],
                ["Loan needed", money(budget.loan_needed)],
                ["LVR", budget.lvr != null ? `${budget.lvr}%` : undefined],
                ["Weekly repayment", budget.repayment_weekly ? `${money(budget.repayment_weekly)}/wk` : undefined],
              ]} />
            </div>
            {(selected.name || chosen.name) && (
              <>
                <div style={S.eyebrow}>Chosen home</div>
                <div style={S.card}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{selected.name || chosen.name}</div>
                  <div style={{ fontSize: 13, color: "#68736E", marginTop: 2 }}>
                    {[chosen.beds && `${chosen.beds} bed`, chosen.baths && `${chosen.baths} bath`, chosen.garage && `${chosen.garage} car`, chosen.house_size_m2 && `${chosen.house_size_m2}m²`].filter(Boolean).join("  ·  ")}
                  </div>
                  {(chosen.floorplan_image_url || chosen.image_url) ? (
                    <img src={chosen.floorplan_image_url || chosen.image_url} crossOrigin="anonymous" alt="Floor plan"
                      style={{ width: "100%", marginTop: 10, border: "1px solid #E8E2D2", borderRadius: 10 }} />
                  ) : null}
                </div>
              </>
            )}
            {wealth.milestones?.length ? (
              <>
                <div style={S.eyebrow}>Wealth projection ({wealth.growth_rate || "moderate"})</div>
                <div style={S.card}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      <tr style={{ color: "#68736E", fontSize: 11 }}><td>Year</td><td style={{ textAlign: "right" }}>Home value</td><td style={{ textAlign: "right" }}>Loan</td><td style={{ textAlign: "right" }}>Equity</td></tr>
                      {wealth.milestones.map((m, i) => (
                        <tr key={i} style={{ fontSize: 13 }}>
                          <td>Y{m.year}</td>
                          <td style={{ textAlign: "right" }}>{money(m.home_value)}</td>
                          <td style={{ textAlign: "right" }}>{money(m.loan_balance)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{money(m.equity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </>
        )}

        {isInvestor && (
          <>
            {Object.keys(iDisc).length > 0 && (
              <>
                <div style={S.eyebrow}>Investor discovery</div>
                <div style={S.card}>
                  <KV items={[
                    ["Objective", iDisc.objective],
                    ["Experience", iDisc.experience],
                    ["Properties owned", iDisc.owned_count],
                    ["Funds", iDisc.funds],
                    ["Using equity", iDisc.use_equity ? "Yes" : undefined],
                    ["Weekly contribution", iDisc.contribution],
                    ["Location stance", iDisc.location_stance],
                    ["Preferred area", iDisc.preferred],
                    ["Horizon", iDisc.horizon],
                    ["Future strategy", iDisc.future_strategy],
                  ]} />
                </div>
              </>
            )}
            {iLoc.suburb && (
              <>
                <div style={S.eyebrow}>Target location</div>
                <div style={S.card}>
                  <KV items={[
                    ["Corridor", iLoc.corridor],
                    ["Region", iLoc.region_name],
                    ["Suburb", iLoc.suburb],
                    ["Median", iLoc.median ? money(iLoc.median) : undefined],
                    ["Rent", iLoc.rent_pw ? `$${iLoc.rent_pw}/wk` : undefined],
                    ["Gross yield", iLoc.gross_yield != null ? `${iLoc.gross_yield}%` : undefined],
                    ["12m growth", iLoc.price_growth_12m != null ? `${iLoc.price_growth_12m}%` : undefined],
                  ]} />
                </div>
              </>
            )}
            {iFloor.name && (
              <>
                <div style={S.eyebrow}>Selected floor plan</div>
                <div style={S.card}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{iFloor.name}</div>
                  <div style={{ fontSize: 13, color: "#68736E" }}>{[iFloor.beds && `${iFloor.beds} bed`, iFloor.baths && `${iFloor.baths} bath`].filter(Boolean).join("  ·  ")}</div>
                </div>
              </>
            )}
            {Object.keys(iFund).length > 0 && (
              <>
                <div style={S.eyebrow}>Funding</div>
                <div style={S.card}>
                  <KV items={[
                    ["Property value", iFund.property_value ? money(iFund.property_value) : undefined],
                    ["Usable equity", iFund.usable_equity ? money(iFund.usable_equity) : undefined],
                    ["Cash deposit", iFund.cash_deposit ? money(iFund.cash_deposit) : undefined],
                    ["Loan (IO)", iFund.loan ? money(iFund.loan) : undefined],
                    ["LVR", iFund.lvr != null ? `${iFund.lvr}%` : undefined],
                  ]} />
                </div>
              </>
            )}
          </>
        )}

        {rewards.length ? (
          <>
            <div style={S.eyebrow}>Rewards &amp; voucher</div>
            <div style={S.card}>
              {rewards.map((r, i) => <div key={i} style={{ fontSize: 13 }}>✓ {r.name} — {money(r.value)}</div>)}
              {voucher.code ? <div style={{ fontSize: 13, marginTop: 6, fontFamily: "monospace" }}>Voucher: {voucher.code} ({money(voucher.total)}{voucher.expires ? `, valid until ${voucher.expires}` : ""})</div> : null}
            </div>
          </>
        ) : null}

        <p style={{ fontSize: 11, color: "#888", lineHeight: 1.6, marginTop: 18 }}>
          Internal consultant document. Figures are indicative only and do not constitute financial advice.
          These figures do not take into account taxes that may apply to the capital gain; the client must seek
          advice from their accountant before using capital growth to pay down a mortgage.
        </p>
        <p style={{ fontSize: 12, color: "#C05A46", marginTop: 6 }}>PropIntellix — Consultant Dossier</p>
      </div>
    </div>
  );
});
